import { api } from "../lib";

// ==================== TYPES ====================

export enum SendoutRequestType {
  PHYSICIAN_REQUEST = "physician_request",
  EXTERNAL_INSTITUTION = "external_institution",
  GCP_CONSULT = "gcp_consult",
  MATERIAL_RETURN = "material_return",
}

export enum SendoutStatus {
  PENDING_APPROVAL = "pending_approval",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
  PENDING_SHIPPING = "pending_shipping",
  SHIPPED = "shipped",
  RECEIVED_PARTIALLY = "received_partially",
  RECEIVED = "received",
}

export enum ExternalInstitutionReason {
  CONTINUATION_OF_CARE = "continuation_of_care",
  LEGAL_REQUEST = "legal_request",
  OUTSIDE_REQUEST_ADDITIONAL_TESTING = "outside_request_additional_testing",
  OTHER = "other",
}

export enum SendOutPriority {
  ROUTINE = "routine",
  URGENT = "urgent",
  STAT = "stat",
}

export interface ExternalInstitution {
  id: string;
  name: string;
  shortName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  contactName: string | null;
  specialties: string | null;
  averageTurnaroundDays: number | null;
  isPreconfigured: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SendOutFollowUp {
  id: string;
  sendOutId: string;
  type: "call" | "email";
  loggedById: string;
  contactPerson: string | null;
  subject: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
  loggedBy?: { id: string; fullName: string };
}

export interface SendOutReceiptLog {
  id: string;
  sendOutId: string;
  receivedById: string;
  receivedAt: string;
  slidesReturned: string[];
  blocksReturned: string[];
  materialsCondition: Record<string, string> | null;
  externalReportFileKey: string | null;
  externalDiagnosis: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  receivedBy?: { id: string; fullName: string };
}

export interface SendOut {
  id: string;
  caseId: string;
  requestType: SendoutRequestType;
  status: SendoutStatus;
  externalInstitutionId: string | null;
  stainProviderId: string | null;
  requestingPhysicianId: string | null;
  requestedById: string;
  approvedById: string | null;
  approvedAt: string | null;
  reason: string | null;
  slidesSent: string[] | null;
  blocksSent: string[] | null;
  reportIncluded: boolean;
  requisitionIncluded: boolean;
  rejectionReason: string | null;
  rejectedById: string | null;
  rejectedAt: string | null;
  requiresApproval: boolean;
  shippedAt: string | null;
  trackingNumber: string | null;
  receivedAt: string | null;
  externalReportReceived: boolean;
  externalReportDate: string | null;
  externalDiagnosis: string | null;
  externalReferenceNumber?: string | null;
  externalInterpretation?: string | null;
  notes: string | null;
  // New tracking fields (C-804)
  expectedReturnDate: string | null;
  receivedById: string | null;
  shippedById: string | null;
  slidesReturned: string[] | null;
  blocksReturned: string[] | null;
  materialsCondition: string | null;
  externalReportFileKey: string | null;
  // Send-out enhancement fields (024-send-out-enhancements)
  externalInstitutionReason?: ExternalInstitutionReason | null;
  externalInstitutionReasonOther?: string | null;
  orderingInstitutionId?: string | null;
  orderingInstitution?: { id: string; name: string } | null;
  contactPerson?: string | null;
  contactPhoneFax?: string | null;
  priority?: SendOutPriority | null;
  authorizationReference?: string | null;
  specialInstructions?: string | null;
  isPendingReview?: boolean | null;
  workflowKind?: "tissue_cypher" | null;
  sourcePanelOrderId?: string | null;
  // Timestamps
  createdAt: string;
  updatedAt: string;
  // Relations
  case?: {
    id: string;
    caseNumber: string;
    externalCaseNumber?: string | null;
    patient?: { firstName: string; lastName: string };
    facility?: { id: string; name: string };
  };
  externalInstitution?: ExternalInstitution;
  stainProvider?: { id: string; name: string; code?: string };
  requestedBy?: { id: string; fullName: string } | null;
  approvedBy?: { id: string; fullName: string } | null;
  requestingPhysician?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  qaReviewer?: { id: string; fullName: string } | null;
  receivedBy?: { id: string; fullName: string } | null;
  shippedBy?: { id: string; fullName: string } | null;
  rejectedBy?: { id: string; fullName: string } | null;
  followUps?: SendOutFollowUp[];
  receiptLogs?: SendOutReceiptLog[];
  // Resolved labels (added for overdue report C-4160)
  slideLabels?: string[];
  blockLabels?: string[];
}

// ===== Resolved materials types (for approval modal) =====

export interface ResolvedSlide {
  id: string;
  number: string;
  barcode: string | null;
  stain?: { code: string; description: string };
}

export interface ResolvedBlock {
  id: string;
  number: string;
  slides: ResolvedSlide[];
}

export interface ResolvedSpecimen {
  specimen: {
    id: string;
    number: string;
    source?: { name: string };
  };
  blocks: ResolvedBlock[];
}

export interface SendOutWithCaseDetails extends SendOut {
  case: SendOut["case"] & {
    patient?: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      sex: string;
      medicalRecordNumber?: string;
    };
    currentStatus?: { name: string };
    pathologist?: { fullName: string };
    isRush?: boolean;
    isHighPriority?: boolean;
  };
  resolvedMaterials: ResolvedSpecimen[];
}

export interface CreateSendOutInput {
  caseId: string;
  requestType: SendoutRequestType;
  externalInstitutionId: string;
  requestingPhysicianId?: string;
  reason?: string;
  slidesSent?: string[];
  blocksSent?: string[];
  reportIncluded?: boolean;
  requisitionIncluded?: boolean;
  requiresApproval?: boolean;
  qaReviewerId?: string;
  notes?: string;
  // Send-out enhancement fields
  externalInstitutionReason?: ExternalInstitutionReason;
  externalInstitutionReasonOther?: string;
  orderingInstitutionId?: string;
  contactPerson?: string;
  contactPhoneFax?: string;
  priority?: SendOutPriority;
  expectedReturnDate?: string;
  authorizationReference?: string;
  specialInstructions?: string;
  duplicateConfirmed?: boolean;
}

export interface LogReceiptInput {
  receivedById?: string;
  receivedDate?: string;
  slidesReturned?: string[];
  blocksReturned?: string[];
  materialsCondition?: string;
  externalReportFileKey?: string;
  externalDiagnosis?: string;
  externalReferenceNumber?: string;
  externalInterpretation?: string;
  notes?: string;
}

export interface FollowUpInput {
  type: "call" | "email";
  contactPerson?: string;
  subject?: string;
  notes: string;
}

export interface QueueSummary {
  pendingApproval: number;
  pendingShipping: number;
  sent: number;
  overdue: number;
  rejected: number;
  returned: number;
  tissueCypher: number;
}

export interface SendOutSummary {
  id: string;
  caseNumber: string;
  patientName: string;
  requestType: SendoutRequestType;
  status: SendoutStatus;
  rejectionReason?: string | null;
  rejectedAt?: string;
  expectedReturnDate?: string | null;
}

export interface NeedingAttentionResult {
  rejected: SendOutSummary[];
  overdue: SendOutSummary[];
  rejectedCount: number;
  overdueCount: number;
}

export interface ShipSendOutInput {
  trackingNumber?: string;
  expectedReturnDate?: string;
}

// ==================== SERVICE ====================

export const sendOutsService = {
  // ===== CRUD =====

  /**
   * Create a new send-out request
   */
  create: async (input: CreateSendOutInput) => {
    return api.post<SendOut>("/send-outs", input);
  },

  /**
   * Get all send-outs for a specific case
   */
  getByCaseId: async (caseId: string) => {
    return api.get<SendOut[]>(`/send-outs/case/${caseId}`);
  },

  /**
   * Get a single send-out by ID
   */
  getById: async (id: string) => {
    return api.get<SendOut>(`/send-outs/${id}`);
  },

  /**
   * Get a single send-out with full details (relations + follow-ups)
   */
  getByIdWithDetails: async (id: string) => {
    return api.get<SendOut>(`/send-outs/${id}/details`);
  },

  /**
   * Get a send-out with enriched case details and resolved materials
   */
  getByIdWithCaseDetails: async (id: string) => {
    return api.get<SendOutWithCaseDetails>(`/send-outs/${id}/case-details`);
  },

  // ===== Queue Views =====

  /**
   * Get send-outs pending approval
   */
  getPendingApproval: async () => {
    return api.get<SendOut[]>("/send-outs/queue/pending-approval");
  },

  /**
   * Get send-outs pending shipping
   */
  getPendingShipping: async () => {
    return api.get<SendOut[]>("/send-outs/queue/pending-shipping");
  },

  getTissueCypher: async () => {
    return api.get<SendOut[]>("/send-outs/queue/tissue-cypher");
  },

  /**
   * Get in-transit send-outs (shipped but not received)
   */
  getInTransit: async () => {
    return api.get<SendOut[]>("/send-outs/queue/in-transit");
  },

  /**
   * Get overdue send-outs
   */
  getOverdue: async () => {
    return api.get<SendOut[]>("/send-outs/queue/overdue");
  },

  /**
   * Get rejected send-outs for audit trail (C-2497)
   */
  getRejected: async () => {
    return api.get<SendOut[]>("/send-outs/queue/rejected");
  },

  /**
   * Get queue summary counts for badges
   */
  getQueueSummary: async () => {
    return api.get<QueueSummary>("/send-outs/queue/summary");
  },

  // ===== Workflow Actions =====

  /**
   * Approve a send-out request
   */
  approve: async (id: string) => {
    return api.post<SendOut>(`/send-outs/${id}/approve`);
  },

  /**
   * Reject a send-out request
   */
  reject: async (id: string, rejectionReason: string) => {
    return api.post<SendOut>(`/send-outs/${id}/reject`, { rejectionReason });
  },

  /**
   * Mark send-out as shipped
   */
  markShipped: async (id: string, data: ShipSendOutInput = {}) => {
    return api.post<SendOut>(`/send-outs/${id}/ship`, data);
  },

  /**
   * Mark send-out as fully received
   */
  markReceived: async (id: string, data: LogReceiptInput = {}) => {
    return api.post<SendOut>(`/send-outs/${id}/receive`, data);
  },

  /**
   * Mark send-out as partially received
   */
  markPartiallyReceived: async (id: string, data: LogReceiptInput = {}) => {
    return api.post<SendOut>(`/send-outs/${id}/receive-partially`, data);
  },

  // ===== Follow-Ups =====

  /**
   * Get follow-ups for a send-out
   */
  getFollowUps: async (id: string) => {
    return api.get<SendOutFollowUp[]>(`/send-outs/${id}/follow-ups`);
  },

  /**
   * Add a follow-up entry to a send-out
   */
  addFollowUp: async (id: string, data: FollowUpInput) => {
    return api.post<SendOutFollowUp>(`/send-outs/${id}/follow-ups`, data);
  },

  // ===== Alert Dismiss =====

  /**
   * Dismiss an overdue alert for 7 days (C-804 §8.2)
   */
  dismissAlert: async (id: string) => {
    return api.post<SendOut>(`/send-outs/${id}/dismiss`);
  },

  // ===== Reference Data =====

  /**
   * Get active external institutions for dropdown
   */
  getInstitutions: async () => {
    return api.get<ExternalInstitution[]>("/send-outs/reference/institutions");
  },

  // ===== File Upload =====

  /**
   * Get presigned URL for uploading an external report PDF
   */
  getReportUploadUrl: async (fileName: string, contentType: string) => {
    return api.post<{ url: string; key: string }>(
      "/send-outs/presigned-url/report",
      { fileName, contentType }
    );
  },

  // ===== Send-out enhancements (024) =====

  /**
   * Resubmit a rejected send-out with updated data
   */
  resubmitSendOut: async (id: string, data: CreateSendOutInput) => {
    return api.post<SendOut>(`/send-outs/${id}/resubmit`, data);
  },

  /**
   * Mark a send-out as reviewed (clears isPendingReview)
   */
  markSendOutReviewed: async (id: string) => {
    return api.post<SendOut>(`/send-outs/${id}/mark-reviewed`);
  },

  /**
   * Get send-outs needing attention (rejected + overdue)
   */
  getNeedingAttention: async () => {
    return api.get<NeedingAttentionResult>("/send-outs/needing-attention");
  },

  /**
   * Get non-overdue shipped send-outs (Sent tab)
   */
  getSent: async () => {
    return api.get<SendOut[]>("/send-outs/queue/sent");
  },

  /**
   * Get returned send-outs (received + received_partially)
   */
  getReturned: async () => {
    return api.get<SendOut[]>("/send-outs/queue/returned");
  },
};
